import Amenities from '@components/HotelCard/Amenities';
import Gallery from '@components/SearchExperiment/Gallery';
import REVIEWS_THRESHOLD_TO_BE_SHOWN from '@constants/yotpo';
import HotelApi from '@customTypes/hotel-api';
import Slide from '@customTypes/slide';
import React, { useEffect, useMemo, useState } from 'react';
import Image from 'next/legacy/image';
import grayHeartIcon from '@assets/images/gray-heart-iconNew.svg';
import redHeartIcon from '@assets/images/red-heart-iconNew.svg';
import FavoritesToastMessage from '@components/MyFavorites/ToastMessage/FavoritesToastMessage';
import AVAILABILITY, {
  PRODUCT_TITLE_CATEGORY_TEXT,
  PRODUCT_CATEGORY_TEXT,
} from '@constants/product';
import Products from '@components/SearchExperiment/HotelCard/Products';
import StarRating from '@components/SearchExperiment/Yotpo/StarRating';
import { useRouter } from 'next/router';
import useIsMobile from '@hooks/useIsMobile';
import LinkButton from '@solar/common/Button/LinkButton';
import ChevronIcon from '@solar/assets/images/directional/chevron';
import { ExperimentType } from '@customTypes/context';
import { getWeightedAmenities } from '@utils/Search/weighted_amenities';
import Amenity from '@customTypes/amenity';
import { isTreatment } from '@helpers/experiment';
import { FreeCancellationText } from '@components/FreeCancellation';
import { HotelCard } from './components';

export type HotelCardProps = {
  hotel: HotelApi;
  isHighlighted?: boolean;
  showFavoriteToastMessage: boolean;
  saveToFavorites: boolean;
  userFavorites: any;
  handleAddingHotelIdToFavorites: (hotel: HotelApi) => void;
  selectedVibes: string[];
  productCategory: string;
  searchedDate: string;
  onClick?: () => void;
  href: string;
  featureFlags?: Record<string, ExperimentType>;
};

interface ImageProps {
  picture: {
    details: { url: string };
    width_375: { url: string };
    url: string;
  };
}

export default function SRPHotelCard({
  hotel,
  isHighlighted = false,
  showFavoriteToastMessage,
  saveToFavorites,
  userFavorites,
  handleAddingHotelIdToFavorites,
  selectedVibes,
  productCategory,
  searchedDate = '',
  onClick = () => {},
  href,
  featureFlags = {},
}: HotelCardProps) {
  const isMobile = useIsMobile({ isSolarSystem: true });
  const router = useRouter();

  const [galleryImages, setGalleryImages] = useState<Slide[]>([]);
  useEffect(() => {
    const images = hotel.image.map((image: ImageProps) => ({
      key: image.picture.details.url,
      url: image.picture.details.url,
      imgClassnames: 'w-full swiper-lazy rounded-xl',
      urlShort: image.picture.url,
    }));
    const productImages = hotel.product_images?.map((image: string) => ({
      key: image,
      url: image,
      imgClassnames: 'w-full swiper-lazy rounded-xl',
      urlShort: image,
    }));
    const hasProductCategory =
      !!PRODUCT_TITLE_CATEGORY_TEXT[productCategory as keyof typeof PRODUCT_CATEGORY_TEXT];

    if (hotel.imageDesktop !== images[0]?.urlShort) {
      const filteredImages = images.filter((item) => item.urlShort !== hotel.imageDesktop);
      filteredImages.unshift({
        key: hotel.imageDesktop,
        url: hotel.imageDesktop,
        imgClassnames: 'w-full swiper-lazy rounded-xl',
        urlShort: hotel.imageDesktop,
      });
      if (hasProductCategory && productImages?.length > 0) {
        filteredImages.unshift(...productImages);
      }
      setGalleryImages(filteredImages);
      return;
    }
    if (hasProductCategory && !isHighlighted && productImages?.length > 0) {
      images.unshift(...productImages);
    }
    setGalleryImages(images);
  }, [
    hotel.image,
    hotel.imageDesktop,
    hotel.name,
    hotel.product_images,
    isHighlighted,
    productCategory,
  ]);

  const vibesBadge = useMemo(() => {
    const transformVibe = (vibe: string) => (vibe === 'Family-Friendly' ? 'Family' : vibe);

    if (!hotel.vibes) return '';
    if (!(hotel.vibes.primary || hotel.vibes.secondary)) return '';
    if (!selectedVibes || !selectedVibes.length) {
      return transformVibe(hotel.vibes.primary || hotel.vibes.secondary || '');
    }
    if (
      hotel.vibes.primary &&
      (selectedVibes.includes(hotel.vibes.primary) ||
        selectedVibes.includes(hotel.vibes.primary.toLocaleLowerCase()))
    ) {
      return transformVibe(hotel.vibes.primary);
    }
    if (
      hotel.vibes.secondary &&
      (selectedVibes.includes(hotel.vibes.secondary) ||
        selectedVibes.includes(hotel.vibes.secondary.toLocaleLowerCase()))
    ) {
      return transformVibe(hotel.vibes.secondary);
    }
    return '';
  }, [hotel.vibes, selectedVibes]);

  const handleAddToFavorite = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleAddingHotelIdToFavorites(hotel);
  };

  const filteredProducts = hotel.products.filter((p) =>
    (!productCategory
      ? true
      : (Array.isArray(p.product_categories) ? p.product_categories : []).some(
        (cat) => cat?.toLowerCase() === productCategory.toLowerCase(),
      )));

  const finalProducts = filteredProducts.length > 0 ? filteredProducts : hotel.products;

  const sortedProducts = useMemo(() => {
    const sortByProductType = (
      a: { product_type_sort_order: number },
      b: { product_type_sort_order: number },
    ) => a.product_type_sort_order - b.product_type_sort_order;

    const hotspotAvailable = finalProducts
      .filter((product) => product.is_hot_spot && product.availability === AVAILABILITY.AVAILABLE)
      .sort(sortByProductType);

    const hotspotUnavailable = finalProducts
      .filter((product) => product.is_hot_spot && product.availability !== AVAILABILITY.AVAILABLE)
      .sort(sortByProductType);

    const availableNonHotspot = finalProducts
      .filter((product) => !product.is_hot_spot && product.availability === AVAILABILITY.AVAILABLE)
      .sort(sortByProductType);

    const unavailableNonHotspot = finalProducts
      .filter((product) => !product.is_hot_spot && product.availability !== AVAILABILITY.AVAILABLE)
      .sort(sortByProductType);

    return [
      ...hotspotAvailable,
      ...hotspotUnavailable,
      ...availableNonHotspot,
      ...unavailableNonHotspot,
    ].slice(0, 3);
  }, [finalProducts]);

  const hotelName = useMemo(() => {
    // If no category or no filtered products, just return the hotel name
    if (!productCategory || filteredProducts.length === 0) {
      return hotel.name;
    }

    // Get the category prefix (e.g., "spa") from the lookup object
    const categoryPrefix =
      PRODUCT_TITLE_CATEGORY_TEXT[productCategory as keyof typeof PRODUCT_TITLE_CATEGORY_TEXT] ||
      '';

    // If the hotel name already starts with the prefix, no need to add it
    if (hotel.name.startsWith(categoryPrefix) || hotel.name.startsWith('Spa')) {
      return hotel.name;
    }

    // Combine the prefix and hotel name
    return `${categoryPrefix}${hotel.name}`;
  }, [hotel.name, productCategory, filteredProducts]);

  const hotelAmenities = useMemo(() => {
    if (isTreatment(featureFlags.srpAmenityHighlights)) {
      const remappedVibes: Amenity[] = [
        ...selectedVibes,
        hotel.vibes.primary,
        hotel.vibes.secondary,
      ]
        .filter((vibe) => !!vibe)
        .map((vibe) => ({
          name: vibe!,
          description: vibe!,
          icon_text: '',
        }));
      return getWeightedAmenities(productCategory, [...hotel.amenities, ...remappedVibes], 3);
    }
    return hotel.amenities;
  }, [
    featureFlags.srpAmenityHighlights,
    hotel.amenities,
    productCategory,
    hotel.vibes.primary,
    hotel.vibes.secondary,
    selectedVibes,
  ]);

  const navigateOnClick = () => {
    onClick();
    if (isMobile) {
      router.push(href);
    } else {
      window.open(href, '_blank');
    }
  };

  const onKeyboardEnterPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      navigateOnClick();
    }
  };

  const hotelLocation = hotel.code ? `${hotel.cityName}, ${hotel.code}` : `${hotel.cityName}`;
  const experiencesCount = finalProducts.length - sortedProducts.length;
  const productCategoryText =
    productCategory && filteredProducts.length > 0
      ? PRODUCT_CATEGORY_TEXT[productCategory as keyof typeof PRODUCT_CATEGORY_TEXT] || ''
      : '';

  return (
    <>
      {showFavoriteToastMessage && <FavoritesToastMessage saveToFavorites={saveToFavorites} />}

      <HotelCard highlight={isHighlighted}>
        <HotelCard.Gallery asChild>
          <div className="relative pointer-events-auto">
            <Gallery
              classes="z-20 desktop:w-4/12 cursor-pointer"
              name={hotel.name}
              images={galleryImages}
              navigateToDetailsPage={navigateOnClick}
              userFavorites={userFavorites}
              handleAddingHotelIdToFavorites={handleAddingHotelIdToFavorites}
              hotel={hotel}
              hasHotelOffer={hotel.hasHotelOffer}
            />
          </div>
        </HotelCard.Gallery>

        <HotelCard.Title
          id={hotel.name}
          title={hotelName}
          onClick={navigateOnClick}
          className="cursor-pointer"
        >
          {hotelName}
        </HotelCard.Title>

        <HotelCard.FavoritesTrigger asChild>
          <button type="button" onClick={handleAddToFavorite} className="z-20">
            <div className="cursor-pointer hidden desktop:block">
              <Image
                src={
                  userFavorites && userFavorites.includes(hotel.id.toString())
                    ? redHeartIcon
                    : grayHeartIcon
                }
                alt="heart"
              />
            </div>
          </button>
        </HotelCard.FavoritesTrigger>

        {hotel.rating && hotel.rating > 0 && (
          <HotelCard.Rating asChild>
            <StarRating
              algoliaRating={hotel.rating}
              algoliaReviews={hotel.reviewsCount}
              reviewsThreshold={REVIEWS_THRESHOLD_TO_BE_SHOWN}
            />
          </HotelCard.Rating>
        )}

        <HotelCard.Location data-testid="hotel-city-and-state" title={hotelLocation}>
          {hotelLocation}
        </HotelCard.Location>

        {vibesBadge && !isTreatment(featureFlags.srpAmenityHighlights) && (
          <HotelCard.Vibes>{vibesBadge}</HotelCard.Vibes>
        )}
        <HotelCard.Amenities className="text-rp-text-secondary">
          <Amenities
            amenities={hotelAmenities}
            variant={isTreatment(featureFlags.srpAmenityHighlights) ? 'expanded' : 'compact'}
            size={isTreatment(featureFlags.srpAmenityHighlights) ? 'small' : 'medium'}
          />
        </HotelCard.Amenities>

        <HotelCard.Products asChild>
          <div
            className="cursor-pointer"
            onClick={navigateOnClick}
            onKeyDown={onKeyboardEnterPress}
            role="button"
            tabIndex={0}
          >
            <Products hotel={hotel} sortedProducts={sortedProducts} searchedDate={searchedDate} />
          </div>
        </HotelCard.Products>
        {experiencesCount > 0 && (
          <HotelCard.Experiences>
            {`+${experiencesCount} more ${productCategoryText} experience${
              experiencesCount > 1 ? 's' : ''
            }`}
          </HotelCard.Experiences>
        )}
        {isTreatment(featureFlags.freeCancellationExperiment) && hotel.can_be_cancelled && (
          <HotelCard.CancellationPolicy className="flex items-center gap-0.5">
            <FreeCancellationText />
          </HotelCard.CancellationPolicy>
        )}
        <HotelCard.DetailsTrigger asChild>
          <LinkButton
            href={href}
            title={`View all ${hotel.name} products`}
            openInNewTab={!isMobile}
            onClick={onClick}
          >
            <div className="flex gap-2 items-center" data-testid="hotel-view-all-btn">
              View All
              <ChevronIcon direction="right" color="#FEFEFE" />
            </div>
          </LinkButton>
        </HotelCard.DetailsTrigger>
      </HotelCard>
    </>
  );
}
